// Mock CMS Data for Blog and Video Library
// This structure is CMS-ready and can be replaced with actual API calls

export const blogPosts = [
  {
    id: 'understanding-neuromuscular-therapy',
    title: 'Understanding Neuromuscular Therapy',
    excerpt: 'Learn about the foundations of neuromuscular therapy and how it can help with chronic pain and mobility issues.',
    category: 'Therapy',
    readTime: '5 min read',
    date: '2024-01-15',
    published: true,
  },
  {
    id: 'nutrition-for-recovery',
    title: 'Nutrition for Recovery',
    excerpt: 'Discover the essential nutrients your body needs for optimal recovery after injury or intense training.',
    category: 'Nutrition',
    readTime: '7 min read',
    date: '2024-01-10',
    published: true,
  },
  {
    id: 'sports-injury-prevention',
    title: 'Sports Injury Prevention Tips',
    excerpt: 'Practical advice for athletes on how to prevent common sports injuries through proper preparation.',
    category: 'Sports',
    readTime: '6 min read',
    date: '2024-01-05',
    published: true,
  },
];

export const videos = [
  {
    id: 'stretching-routine-beginners',
    title: 'Stretching Routine for Beginners',
    description: 'A gentle 10-minute stretching routine suitable for all fitness levels.',
    category: 'Exercise',
    duration: '10:24',
    date: '2024-01-12',
    published: true,
  },
  {
    id: 'understanding-food-intolerances',
    title: 'Understanding Food Intolerances',
    description: 'An overview of common food intolerances and how they affect your body.',
    category: 'Nutrition',
    duration: '15:30',
    date: '2024-01-08',
    published: true,
  },
  {
    id: 'self-massage-techniques',
    title: 'Self-Massage Techniques',
    description: 'Learn effective self-massage techniques for relieving muscle tension at home.',
    category: 'Therapy',
    duration: '12:45',
    date: '2024-01-03',
    published: true,
  },
];

export const services = [
  {
    id: 'injury-sports-treatments',
    slug: 'injury-sports',
    title: 'Injury & Sports Treatments',
    shortDescription: 'Hands-on therapy to reduce pain, improve mobility, and restore function.',
    fullDescription: `We provide hands-on physical therapy for a wide range of musculoskeletal conditions and sports injuries. Treatment is focused on identifying the root cause of pain and dysfunction — not just addressing symptoms — so that lasting improvement can be made.

Each session is tailored to the individual and may include a combination of massage, mobilisation, dry needling (acupuncture), cupping, and exercise guidance. We work with clients of all levels, from those recovering from everyday injuries to competitive athletes looking to return to full performance.

Sessions typically begin with a thorough assessment followed by targeted treatment. Where appropriate, we will recommend specific exercises or stretches to support recovery between sessions.

Please note: clients under 16 must be accompanied by a parent or guardian.`,
    benefits: [
      'Pain reduction and management',
      'Improved range of motion and mobility',
      'Faster recovery from injury',
      'Enhanced athletic performance',
      'Prevention of recurring injuries',
      'Exercise and stretch guidance for home care',
    ],
    conditions: [
      'Headaches and migraines',
      'Neck and shoulder tension',
      'Back pain (upper and lower)',
      'Plantar fascia issues',
      'Glute and hip pain',
      'Sports injuries and strains',
      'Joint stiffness and reduced mobility',
      'Postural issues',
    ],
    pricing: [
      { name: '1 Hour Session', price: '€65' },
      { name: '30 Minute Session', price: '€40' },
    ],
  },
  {
    id: 'nutritional-therapy',
    slug: 'nutritional-therapy',
    title: 'Nutritional Therapy',
    shortDescription: 'Personalised nutrition support addressing digestion, hormones, energy, and long-term health.',
    fullDescription: `Nutritional therapy is a personalised, evidence-based approach to health that uses diet, lifestyle, and targeted supplementation to address the root causes of illness and support optimal wellbeing. Ali completed a comprehensive 3-year qualification in Nutritional Therapy and is a member of the Nutritional Therapists of Ireland (NTOI).

This service is suited to anyone looking to improve their health — whether you are dealing with a specific condition, want to optimise performance, or simply feel that something isn't right and want expert guidance.

We use a range of functional testing methods, including stool analysis, urinary microbiome testing, and other targeted assessments, to build a detailed picture of what is happening in the body. Based on the results, a personalised plan is created that includes dietary changes, supplement recommendations, and lifestyle adjustments.

The approach is always individualised. There are no generic meal plans or one-size-fits-all solutions. We work closely with each client to ensure the plan is practical, sustainable, and effective for the long term.`,
    benefits: [
      'Improved digestive health and comfort',
      'Balanced hormones',
      'Increased energy and reduced fatigue',
      'Better sleep quality',
      'Weight management support',
      'Enhanced immune function',
      'Support for fertility and pregnancy',
      'Improved sports performance and recovery',
    ],
    conditions: [
      'Digestive issues (IBS, IBD, bloating, reflux)',
      'Hormonal health and imbalances',
      'Fatigue and low energy',
      'Cholesterol and fatty liver',
      'Autoimmune conditions',
      'Fertility and pregnancy support',
      'Sports performance nutrition',
      'Skin conditions and food intolerances',
    ],
    pricing: [
      { name: 'Nutritional Therapy Package (3 sessions)', price: '€250' },
      { name: 'Food Intolerance Testing', price: '€110' },
      { name: 'Mini Health Review', price: '€80' },
    ],
  },
  {
    id: 'once-off-services',
    slug: 'once-off',
    title: 'Once Off Services',
    shortDescription: 'Targeted single sessions for specific health concerns or guidance.',
    fullDescription: `These are standalone sessions designed for clients who need focused support on a specific topic without committing to an ongoing treatment package. Each session is self-contained and delivers clear, actionable guidance.

Sports Nutrition Session (€120): Ideal for athletes and active individuals looking to optimise their nutrition for training, competition, or recovery. This session covers fuelling strategies, hydration, supplement guidance, and practical meal planning tailored to your sport and goals.

Health Consultation (€80): A focused review of your current health concerns. We will assess your situation and provide clear dietary and lifestyle recommendations. This is a good starting point if you're unsure whether a full nutritional therapy package is right for you.

Pregnancy Treatment (€45): A gentle, supportive hands-on treatment session designed specifically for pregnant clients. Focused on relieving common pregnancy-related discomfort such as back pain, tension, and swelling.`,
    benefits: [
      'Expert assessment of your specific concern',
      'Clear, actionable recommendations',
      'No ongoing commitment required',
      'Focused and efficient',
    ],
    conditions: [
      'Sports nutrition and fuelling',
      'General health review',
      'Pregnancy-related discomfort',
      'Dietary questions and guidance',
    ],
    pricing: [
      { name: 'Sports Nutrition Session', price: '€120' },
      { name: 'Health Consultation', price: '€80' },
      { name: 'Pregnancy Treatment', price: '€45' },
    ],
  },
  {
    id: 'talks-workshops',
    slug: 'talks',
    title: 'Talks & Workshops',
    shortDescription: 'Group sessions for sports teams, organisations, and wellness events.',
    fullDescription: `Ali is available for group talks and workshops on a range of health, nutrition, and wellness topics. Sessions are tailored to the audience and designed to be engaging, practical, and informative.

These are well suited for sports clubs looking to educate athletes and coaches on nutrition for performance and recovery, wellness groups interested in topics like gut health, energy, or hormonal balance, and community organisations hosting health-focused events or talks.

Each session is built around the group's specific interests and needs. Ali brings real-world experience and a grounded, accessible style to every presentation — focusing on practical takeaways that attendees can apply immediately.`,
    benefits: [
      'Engaging, expert-led sessions',
      'Tailored to your group\'s specific needs',
      'Practical, actionable information',
      'Interactive Q&A opportunities',
    ],
    topics: [
      'Nutrition for sports performance',
      'Injury prevention and recovery',
      'Gut health and digestion',
      'Understanding food and mood',
      'Workplace wellness and energy',
      'Hormonal health basics',
    ],
    pricing: [
      { name: 'Talk / Workshop', price: '€250' },
    ],
  },
];

export const testimonials = [
  {
    id: 1,
    name: '',
    content: '',
    service: '',
  },
  {
    id: 2,
    name: '',
    content: '',
    service: '',
  },
  {
    id: 3,
    name: '',
    content: '',
    service: '',
  },
];

export const practitioners = [
  {
    id: 'ali-dorgan',
    name: 'Ali Dorgan',
    title: 'Neuromuscular & Nutritional Therapist',
    bio: 'We work closely with clients to provide personalised treatment and nutrition support. Due to high demand, availability is managed directly.',
    isLead: true,
    bookingType: 'whatsapp',
    whatsappLink: 'https://wa.me/353877727580',
  },
  {
    id: 'brian-lawton',
    name: 'Brian Lawton',
    title: 'Physical Therapist',
    bio: 'Over 25 years of professional practice. Extensive experience with athletes across multiple sports and competitive levels.',
    isLead: false,
    bookingType: 'calendar',
    bookingLink: 'https://www.fresha.com/book-now/alidorgan-nt-and-nmt-g0b0bzyf/services?lid=206215&eid=611079&oiid=sv%3A11859734&share=true&pId=192546',
  },
  {
    id: 'joy',
    name: 'Joy',
    title: 'Neuromuscular Therapist',
    bio: 'NTC and HCD trained. Focuses on alleviating soft tissue pain while improving overall function. Also a qualified yoga teacher.',
    isLead: false,
    bookingType: 'calendar',
    bookingLink: 'https://www.fresha.com/book-now/alidorgan-nt-and-nmt-g0b0bzyf/services?lid=206215&eid=611079&oiid=sv%3A23043269&share=true&pId=192546',
  },
];

export const contactInfo = {
  email: 'alisnutrition@gmail.com',
  phone: '+353 87 772 7580',
  whatsapp: 'https://wa.me/353877727580?text=Hi%20Ali%2C%20I%27d%20like%20to%20enquire%20about%20your%20services.',
  location: 'Newtown, Cobh, Cork',
};

export const siteConfig = {
  name: 'Ali Dorgan',
  tagline: 'Neuromuscular & Nutritional Therapy',
  credentials: 'Dip NT., BA Soc. Science | Member of NTOI | Qualified Neuromuscular Therapist',
};
